/**
 * Strategic Funds API Service
 *
 * SWR hooks for fetching 7 strategic fund data
 */

import useSWR from 'swr'
import { apiClient } from '../../shared/api/client'
import type { StrategicFundsOverview } from '../../types/modules'

/**
 * Fetch strategic funds overview for a given year
 *
 * GET /strategic-funds?year={year}
 */
export function useStrategicFundsOverview(year: number = 2567) {
  const { data, error, isLoading, mutate } = useSWR<StrategicFundsOverview>(
    [`/strategic-funds`, { year: year.toString() }],
    apiClient.fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: 5 * 60 * 1000, // 5 minutes
    }
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch strategic fund data for specific district
 *
 * GET /strategic-funds/by-district/{districtId}?year={year}
 */
export function useStrategicFundsByDistrict(districtId: number, year: number = 2567) {
  const { data, error, isLoading, mutate } = useSWR<StrategicFundsOverview>(
    districtId ? [`/strategic-funds/by-district/${districtId}`, { year: year.toString() }] : null,
    apiClient.fetcher
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch specific strategic fund details
 *
 * GET /strategic-funds/{fundId}?year={year}
 */
export function useStrategicFundById(fundId: number, year: number = 2567) {
  const { data, error, isLoading, mutate } = useSWR(
    fundId ? [`/strategic-funds/${fundId}`, { year: year.toString() }] : null,
    apiClient.fetcher
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch performance ranking of all 7 strategies
 *
 * GET /strategic-funds/performance?year={year}
 */
export function useStrategicFundsPerformance(year: number = 2567) {
  const { data, error, isLoading, mutate } = useSWR<{
    year: number
    strategy_ranking: Array<{
      strategy_number: number
      name_th: string
      performance_score: string
      rank: number
    }>
  }>(
    [`/strategic-funds/performance`, { year: year.toString() }],
    apiClient.fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 60 * 1000, // Cache for 1 minute
    }
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch strategic funds list (reference data)
 *
 * GET /strategic-funds-list
 */
export function useStrategicFundsList() {
  const { data, error, isLoading } = useSWR(
    '/strategic-funds-list',
    apiClient.fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshInterval: 60 * 60 * 1000, // Cache for 1 hour
    }
  )

  return {
    data,
    error,
    isLoading,
  }
}
